import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { X } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';

interface CampaignSummary {
  id: string;
  name: string;
}

/**
 * Story 46.3 — campaign-context filter pill.
 *
 * When the URL includes ?campaignId=<uuid>, this pill shows the campaign
 * name with an ✕ that clears the parameter. Returns null if no
 * campaignId is set so callers can render it unconditionally.
 *
 * The pill also offers a "Back to campaign" link to /campaigns/:id so
 * users can jump from a flat-list scope back to the hub.
 */
export function CampaignFilterPill() {
  const [searchParams, setSearchParams] = useSearchParams();
  const campaignId = searchParams.get('campaignId');
  const [name, setName] = useState<string | null>(null);

  useEffect(() => {
    if (!campaignId) {
      setName(null);
      return;
    }
    apiClient
      .get<CampaignSummary>(`/campaigns/${campaignId}`)
      .then(({ data }) => setName(data.name))
      .catch(() => setName('Unknown campaign'));
  }, [campaignId]);

  if (!campaignId) return null;

  return (
    <div className="flex items-center gap-2">
      <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
        Campaign: {name ?? '…'}
        <button
          type="button"
          onClick={() => {
            const next = new URLSearchParams(searchParams);
            next.delete('campaignId');
            setSearchParams(next);
          }}
          className="rounded-full p-0.5 hover:bg-primary/20"
          aria-label="Clear campaign filter"
        >
          <X className="h-3 w-3" />
        </button>
      </span>
      {name && (
        <Link
          to={`/campaigns/${campaignId}`}
          className="text-xs text-muted-foreground hover:text-primary hover:underline"
        >
          ↩ Back to campaign
        </Link>
      )}
    </div>
  );
}
